import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { concat, forkJoin, Observable, of, zip } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { DataService } from 'src/app/shared/data.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class NoiseCo2Service {
  private endPoint = `${environment.api}/noise-co-2-s`;

  private dyName = 'noise_co_2';

  private updateSampleChartsUrl = `${environment.api}/sample-charts`;

  private updateMeasurementPointsUrl = `${environment.api}/measurement-points`;

  constructor(private http: HttpClient, private dataService: DataService) {}

  fetchData(): Observable<any> {
    return this.dataService.selectedYear.pipe(
      switchMap((dy) => {
        if (dy[this.dyName]) {
          return this.http.get(`${this.endPoint}/${dy[this.dyName]}`).pipe(
            switchMap((noiseCo2Data: any) => {
              console.log('Fetched noise_co_2 data:', noiseCo2Data);
              const sampleFields = ['workplace_noise', 'outside_noise'];
              const sampleRequests: Observable<any>[] = [];

              // For each sample field, if it has an ID, fetch the full sample-chart
              sampleFields.forEach(fieldName => {
                const sampleId = noiseCo2Data[fieldName];
                if (sampleId && typeof sampleId === 'number') {
                  console.log(`Fetching sample-chart ${sampleId} for field ${fieldName}`);
                  sampleRequests.push(
                    this.http.get(`${this.updateSampleChartsUrl}/${sampleId}`).pipe(
                      map(sampleData => ({ fieldName, sampleData }))
                    )
                  );
                }
              });

              // If no samples to fetch, return noise_co_2 data as-is
              if (sampleRequests.length === 0) {
                console.log('No sample-charts to fetch');
                return of(noiseCo2Data);
              }

              // Fetch all sample-charts in parallel
              return forkJoin(sampleRequests).pipe(
                map(samples => {
                  console.log('Fetched samples:', samples);
                  // Replace IDs with full sample objects
                  samples.forEach(({ fieldName, sampleData }) => {
                    noiseCo2Data[fieldName] = sampleData;
                  });
                  console.log('Final noise_co_2 data with populated samples:', noiseCo2Data);
                  return noiseCo2Data;
                })
              );
            })
          );
        } else {
          return this.http.post(`${this.endPoint}`, {
            data_year: dy.id,
          });
        }
      })
    );
  }

  updateData(formValues: any): Observable<any[]> {
    console.log('NoiseCo2Service.updateData called with:', formValues);

    for (const key of Object.keys(formValues)) {
      if (formValues[key].data?.id === undefined) {
        console.log('Deleting formValue without id:', key, formValues[key]);
        delete formValues[key];
      }
    }

    // Build map of field names to sample IDs (for noise samples)
    const fieldToSampleId: { [key: string]: number } = {};
    for (const key of Object.keys(formValues)) {
      if (key !== 'formData' && formValues[key].data?.id) {
        fieldToSampleId[key] = formValues[key].data.id;
      }
    }

    // Include CO2 data from formData if present
    const co2Data: { [key: string]: any } = {};
    if (formValues.formData?.data) {
      const formData = formValues.formData.data;
      // Extract all fields from formData except id, created_at, updated_at, data_year
      for (const key of Object.keys(formData)) {
        if (!['id', 'created_at', 'updated_at', 'data_year'].includes(key)) {
          co2Data[key] = formData[key];
        }
      }
    }

    console.log('Field to sample ID mapping:', fieldToSampleId);
    console.log('CO2 data:', co2Data);

    const samplesAndPoints = this.splitFormValues(formValues);

    // Get noise_co_2 ID from current year using Observable pattern
    return this.dataService.selectedYear.pipe(
      switchMap((dy) => {
        const updateRequests: Observable<any>[] = [];
        const noiseCo2Id = dy[this.dyName];

        // Update sample-charts WITHOUT noise_co_2 reference
        samplesAndPoints.sampleCharts.forEach((sample) => {
          if (sample.id) {
            console.log(`Updating sample-chart ${sample.id}:`, sample);
            updateRequests.push(
              this.http.put(`${this.updateSampleChartsUrl}/${sample.id}`, sample)
            );
          }
        });

        samplesAndPoints.measurementPoints.forEach((mp) => {
          if (mp.id) {
            console.log(`Updating measurement-point ${mp.id}:`, JSON.stringify(mp, null, 2));
            updateRequests.push(
              this.http.put(`${this.updateMeasurementPointsUrl}/${mp.id}`, mp).pipe(
                tap({
                  error: (error) => {
                    console.error(`Failed to update measurement-point ${mp.id}:`, error);
                    console.error('Measurement point data that failed:', JSON.stringify(mp, null, 2));
                    if (error.error?.data) {
                      console.error('Validation error details:', error.error.data);
                    }
                  }
                })
              )
            );
          }
        });

        // Update samples and measurement points first, then noise_co_2 table
        return (updateRequests.length > 0 ? forkJoin(updateRequests) : of([])).pipe(
          switchMap((sampleResults) => {
            console.log('Sample updates done. Now updating noise_co_2 table with:', { ...fieldToSampleId, ...co2Data });
            return this.http.put(`${this.endPoint}/${noiseCo2Id}`, { ...fieldToSampleId, ...co2Data }).pipe(
              tap((response) => console.log('Noise/CO2 record updated:', response)),
              map((noiseCo2Response) => [...sampleResults, noiseCo2Response])
            );
          })
        );
      })
    );
  }

  private splitFormValues(formValues: any): {
    sampleCharts: any[];
    measurementPoints: any[];
  } {
    const sampleCharts: any[] = [];
    const measurementPoints: any[] = [];
    for (const key of Object.keys(formValues)) {
      if (formValues[key].data && Object.keys(formValues[key].data).length > 0) {
        if (formValues[key].data.points) {
          const points = formValues[key].data.points as Array<any>;
          delete formValues[key].data.points;
          points.forEach((p) => measurementPoints.push(p));
        }
        sampleCharts.push(formValues[key].data);
      }
    }
    return { sampleCharts, measurementPoints };
  }
}
